import { Answer, Question } from '@/playlist/types';

import type { MessageButton, TutorMessage } from '@/features/ai-tutor/types';

export function isButtonJson(text: string) {
	try {
		const arr = JSON.parse(text);
		return Array.isArray(arr) && arr.every((btn) => btn.action && btn.text);
	} catch {
		return false;
	}
}

export function appendButtons(message: TutorMessage, buttons: MessageButton[]): TutorMessage {
	return {
		...message,
		button: [...(message.button || []), ...buttons],
	};
}

export function formatUserAnswer(answer: string): string {
	if (answer.startsWith('question_doubt: ')) {
		return '\u{1F4A1} ' + answer.replace(/^question_doubt: /, '');
	}
	if (answer.startsWith('question_doubt_enabler: ')) {
		return '\u{1F914} ' + answer.replace(/^question_doubt_enabler: /, '');
	}
	return answer;
}

export function getButtonFromText(text: string): { buttons: MessageButton[] } | null {
	if (!isButtonJson(text)) return null;

	const buttons = JSON.parse(text);
	return {
		buttons: buttons.map((btn: { text: string; param: string; type: string }) => ({
			buttonText: btn.text,
			param: btn.param,
			buttonAction: btn.type,
		})),
	};
}

function getPlainTextToHtmlIndexMap(html: string) {
	const map = [];
	let plainIndex = 0;

	for (let htmlIndex = 0; htmlIndex < html.length; htmlIndex++) {
		if (html[htmlIndex] !== '<') {
			map[plainIndex] = htmlIndex;
			plainIndex++;
			continue;
		}

		const tagEndIndex = html.indexOf('>', htmlIndex);
		if (tagEndIndex === -1) break;

		const tag = html.slice(htmlIndex, tagEndIndex + 1);
		if (/^<br\s*\/?>$/i.test(tag)) {
			map[plainIndex] = htmlIndex;
			plainIndex++;
		}
		htmlIndex = tagEndIndex;
	}

	map[plainIndex] = html.length;
	return map;
}

function htmlToComparableText(html: string) {
	return html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '');
}

function getOverlapLength(existingText: string, nextText: string) {
	const existingComparableText = htmlToComparableText(existingText);
	const nextComparableText = htmlToComparableText(nextText);
	const maxLength = Math.min(existingComparableText.length, nextComparableText.length);

	for (let length = maxLength; length >= 8; length--) {
		const existingSuffix = existingComparableText.slice(-length);
		const nextPrefix = nextComparableText.slice(0, length);
		if (existingSuffix === nextPrefix) return length;
	}

	return 0;
}

export function mergeOverlappingText(existingText: string, nextText: string) {
	const overlapLength = getOverlapLength(existingText, nextText);
	if (!overlapLength) return existingText + nextText;

	const existingPlainText = htmlToComparableText(existingText);
	const existingPlainTextToHtmlIndexMap = getPlainTextToHtmlIndexMap(existingText);
	const overlapStartPlainIndex = existingPlainText.length - overlapLength;
	const overlapStartHtmlIndex =
		existingPlainTextToHtmlIndexMap[overlapStartPlainIndex] ?? existingText.length;

	return existingText.slice(0, overlapStartHtmlIndex) + nextText;
}

const createAnswersDisplayString = (answers: Answer[]): string => {
	return answers.reduce((str, answer, index) => {
		return str + answer.answer_display + (index < answers.length - 1 ? ', ' : '');
	}, '');
};

interface ChatTutorQuestionData {
	id: number;
	question: string;
	answers: string;
	correct_answers: string;
}

export const formatChatTutorQuestionData = (question: Question): ChatTutorQuestionData => {
	const questionDisplay = question.question_display;
	const displayText = Array.isArray(questionDisplay) ? questionDisplay[0] : questionDisplay;

	return {
		id: question.id,
		question: `${question.title} ${displayText || ''}`,
		answers: createAnswersDisplayString(question.answers),
		correct_answers: createAnswersDisplayString(
			// Find all correct answers
			question.answers.filter((answer) => answer.is_correct),
		),
	};
};
